import { dateI18n } from '@wordpress/date'
import {
  DATE_FORMAT_DEFAULT,
  DATE_SEPARATOR_DEFAULT,
  TIME_FORMAT_DEFAULT,
  TIME_SEPARATOR_DEFAULT
} from '../constants/datetime'
import { _x, sprintf } from '@wordpress/i18n'

// timezone: IANA id delivered by the Hub — event times always render as wall-clock
// time in that zone. undefined keeps dateI18n's default (the WP site zone).

export function getDateString(date?: Date, format?: string, timezone?: string): string {
  if (!date) {
    return ''
  }
  const dateFormat = format || DATE_FORMAT_DEFAULT
  return dateI18n(dateFormat, date, timezone)
}

export function getDateRangeString(
  start?: Date,
  end?: Date,
  startFormat?: string,
  endFormat?: string,
  separator?: string,
  timezone?: string
): string {
  if (!(start && end)) {
    return ''
  }
  const sep = separator ?? DATE_SEPARATOR_DEFAULT
  return `${getDateString(start, startFormat, timezone)} ${sep} ${getDateString(end, endFormat, timezone)}`
}

export function getTimeString(date: Date, format?: string, timezone?: string): string {
  return dateI18n(format || TIME_FORMAT_DEFAULT, date, timezone)
}

export function getTimeRangeString(
  start: Date,
  end: Date,
  format?: string,
  timezone?: string
): string {
  return `${getTimeString(start, format, timezone)} ${TIME_SEPARATOR_DEFAULT} ${getTimeString(end, format, timezone)}`
}

export function formatEventTime(start?: number, end?: number, noStartTime?: boolean, noEndTime?: boolean, timezone?: string): string {
  const hasStart = !noStartTime && !!start
  const hasEnd = !noEndTime && !!end

  if (!hasStart && !hasEnd) return ''

  const startDate = start ? new Date(start) : undefined
  const endDate = end ? new Date(end) : undefined

  if (hasStart && hasEnd && startDate && endDate) {
    return getTimeRangeString(startDate, endDate, undefined, timezone)
  }
  if (hasStart && startDate) {
    return getTimeString(startDate, undefined, timezone)
  }
  if (hasEnd && endDate) {
    return getTimeString(endDate, undefined, timezone)
  }
  return ''
}

export function formatEventDate(start?: number, end?: number, timezone?: string) {
  const format = _x('d M Y', 'Event Date', 'icf')
  const formatWithoutYear = _x('d M', 'Event Date Without Year', 'icf')

  if (!start) {
    return ''
  }
  const startDate = new Date(start)

  if (!end) {
    return getDateString(startDate, format, timezone)
  }
  const endDate = new Date(end)

  // Same-day/year must be decided in the zone the dates render in, not the browser zone.
  const startDay = dateI18n('Y-m-d', startDate, timezone)
  const endDay = dateI18n('Y-m-d', endDate, timezone)
  const sameDay = startDay === endDay
  const sameYear = startDay.slice(0, 4) === endDay.slice(0, 4)

  return sameDay
    ? getDateString(startDate, format, timezone)
    : getDateRangeString(
        startDate,
        endDate,
        sameYear ? formatWithoutYear : format,
        format,
        undefined,
        timezone
      )
}

/**
 * "Ortszeit São Paulo"-label for times rendered in a Hub-delivered zone. Empty when the
 * zone matches the site zone's wall time at the reference date — equal-offset zones like
 * Europe/Zurich vs. Europe/Berlin need no label.
 */
export function getEventTimezoneLabel(timezone?: string | null, reference?: Date): string {
  if (!timezone) {
    return ''
  }
  const ref = reference ?? new Date()
  if (dateI18n('Y-m-d H:i', ref, timezone) === dateI18n('Y-m-d H:i', ref, undefined)) {
    return ''
  }
  const city = timezone.split('/').pop()?.replace(/_/g, ' ') || timezone
  return sprintf(_x('local time %s', 'Event Timezone Label', 'icf'), city)
}

export function commaList(array: (string | null | undefined)[]): string[] {
  const filteredArray = array.filter(Boolean)
  const lastIdx = filteredArray.length - 1
  return filteredArray.map((value, index) => {
    return index < lastIdx ? `${value}, ` : value
  })
}

export function formatDistance(distance: number): string {
  if (!Number.isFinite(distance)) {
    return ''
  }
  return `${distance.toFixed(0)} ${_x('km', 'Geo Distance', 'icf')}`
}

type Coordinates = {
  latitude: number
  longitude: number
}

export function createGoogleMapsUrl(
  coordinates: Coordinates | null | undefined,
  mapsUrl?: string | null | undefined
): string {
  if (mapsUrl) return mapsUrl
  if (!coordinates?.latitude || !coordinates?.longitude) return ''
  return `https://www.google.com/maps/search/?api=1&query=${coordinates.latitude}%2C${coordinates.longitude}`
}
